<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
// Section 7 — Why Choose E-SCHOOL Stats
// Reference: 7. HOME.png

import { ref, computed, onMounted } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';

import iconTeaching from '~/assets/icons/teaching.svg?url';
import iconStudent from '~/assets/icons/student.svg?url';
import iconQuiz from '~/assets/icons/quiz.svg?url';

const { t } = useI18n();

const statsRef = ref<HTMLElement | null>(null);

const countCourses = ref(0);
const countStudents = ref(0);
const countLessons = ref(0);

const statsData = {
  courses: 1200,
  students: 1030000,
  lessons: 44000,
};

const displayCourses = computed(() => {
  const val = countCourses.value;
  if (val >= 1000) {
    return (val / 1000).toFixed(1) + 'K+';
  }
  return val.toString();
});

const displayStudents = computed(() => {
  const val = countStudents.value;
  if (val >= 1000000) {
    return (val / 1000000).toFixed(2) + 'M+';
  } else if (val >= 1000) {
    return Math.floor(val / 1000) + 'K+';
  }
  return val.toString();
});

const displayLessons = computed(() => {
  const val = countLessons.value;
  if (val >= 1000) {
    return Math.floor(val / 1000) + 'K+';
  }
  return val.toString();
});

const hasAnimated = ref(false);

const animateValue = (refVal: { value: number }, start: number, end: number, duration: number) => {
  const range = end - start;
  let startTime: number | null = null;

  const step = (timestamp: number) => {
    if (!startTime) startTime = timestamp;
    const progress = Math.min((timestamp - startTime) / duration, 1);
    refVal.value = Math.floor(progress * range + start);
    if (progress < 1) {
      requestAnimationFrame(step);
    } else {
      refVal.value = end;
    }
  };

  requestAnimationFrame(step);
};

onMounted(() => {
  if (statsRef.value) {
    useIntersectionObserver(
      statsRef,
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const isIntersecting = entry.isIntersecting;
        if (isIntersecting && !hasAnimated.value) {
          hasAnimated.value = true;
          // Animate counts over 2 seconds (2000ms)
          animateValue(countCourses, 0, statsData.courses, 2000);
          animateValue(countStudents, 0, statsData.students, 2000);
          animateValue(countLessons, 0, statsData.lessons, 2000);
        }
      },
      { threshold: 0.1 }
    );
  }
});
</script>

<template>
  <!-- Section 7 — Why Choose E-SCHOOL Stats -->
  <!-- Reference: 7. HOME.png -->
  <section
    ref="statsRef"
    class="flex min-h-[calc(100vh-72px)] w-full flex-col justify-center bg-[#ece9e5] py-10 sm:min-h-0 sm:py-16"
  >
    <div class="mx-auto w-full max-w-360 px-6 md:px-20">
      <!-- Title -->
      <h2 class="mb-10 text-center text-[26px] font-extrabold text-gray-900 sm:mb-16 sm:text-[32px] md:text-[36px]">
        {{ t('stats.title') }}
      </h2>

      <!-- 3-col stats -->
      <div class="grid grid-cols-1 items-stretch divide-y divide-gray-300 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        <!-- Courses -->
        <div class="flex flex-1 flex-col items-center px-4 py-4 sm:px-8 sm:py-8">
          <div
            class="flex h-24 w-24 items-center justify-center rounded-full border-2 border-gray-800 sm:h-32.5 sm:w-32.5"
          >
            <img :src="iconTeaching" alt="Courses" class="h-12 w-12 object-contain brightness-0 sm:h-16 sm:w-16" />
          </div>
          <span class="mt-4 text-[17px] font-bold text-gray-900 sm:mt-6 sm:text-[18px]">{{ t('stats.courses') }}</span>
          <span class="mt-2 text-[38px] leading-none font-normal text-gray-900 sm:mt-3 sm:text-[44px]">{{
            displayCourses
          }}</span>
        </div>

        <!-- Students -->
        <div class="flex flex-1 flex-col items-center px-4 py-4 sm:px-8 sm:py-8">
          <div
            class="flex h-24 w-24 items-center justify-center rounded-full border-2 border-gray-800 sm:h-32.5 sm:w-32.5"
          >
            <img :src="iconStudent" alt="Students" class="h-12 w-12 object-contain brightness-0 sm:h-16 sm:w-16" />
          </div>
          <span class="mt-4 text-[17px] font-bold text-gray-900 sm:mt-6 sm:text-[18px]">{{ t('stats.students') }}</span>
          <span class="mt-2 text-[38px] leading-none font-normal text-gray-900 sm:mt-3 sm:text-[44px]">{{
            displayStudents
          }}</span>
        </div>

        <!-- Lessons -->
        <div class="flex flex-1 flex-col items-center px-4 py-4 sm:px-8 sm:py-8">
          <div
            class="flex h-24 w-24 items-center justify-center rounded-full border-2 border-gray-800 sm:h-32.5 sm:w-32.5"
          >
            <img :src="iconQuiz" alt="Lessons" class="h-12 w-12 object-contain brightness-0 sm:h-16 sm:w-16" />
          </div>
          <span class="mt-4 text-[17px] font-bold text-gray-900 sm:mt-6 sm:text-[18px]">{{ t('stats.lessons') }}</span>
          <span class="mt-2 text-[38px] leading-none font-normal text-gray-900 sm:mt-3 sm:text-[44px]">{{
            displayLessons
          }}</span>
        </div>
      </div>
    </div>
  </section>
</template>
